import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import SitemapUrlModal from './SitemapUrlModal';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Компонент для сортируемого ключевого слова
const SortableKeywordItem = ({ keyword, index, onKeywordChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast, currentLang }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: `keyword-${index}`,
    data: {
      type: 'keyword',
      index: index,
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`transition-all duration-200 ${
        isDragging ? 'shadow-xl scale-105 rotate-2 z-10' : ''
      }`}
    >
      <div className="flex items-center space-x-2">
        {/* Кнопки сортировки для мобильных */}
        <div className="flex flex-col gap-0.5 sm:hidden">
          <button
            onClick={() => onMoveUp(index)}
            disabled={isFirst}
            className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title={currentLang === 'ru' ? 'Переместить вверх' : 'Move up'}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button
            onClick={() => onMoveDown(index)}
            disabled={isLast}
            className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title={currentLang === 'ru' ? 'Переместить вниз' : 'Move down'}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Иконка для перетаскивания */}
        <div
          {...listeners}
          className="hidden sm:flex flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 rounded-full items-center justify-center cursor-move transition-all duration-200 hover:scale-110 active:scale-95 touch-manipulation"
          title={currentLang === 'ru' ? 'Перетащить для сортировки' : 'Drag to sort'}
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </div>

        <input
          type="text"
          value={keyword}
          onChange={(e) => onKeywordChange(index, e.target.value)}
          className="flex-1 p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-colors"
          placeholder={currentLang === 'ru' ? 'Введите ключевое слово' : 'Enter keyword'}
        />
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="p-2 sm:p-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex-shrink-0"
          title={currentLang === 'ru' ? 'Удалить' : 'Remove'}
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const EditPageModal = ({ page, siteId, site, isOpen, onClose, onPageUpdated }) => {
  const { currentLang, fetchWithAuth, API_URL, getCsrfToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    url: '',
    weight: 0,
    text: '',
    keywords: []
  });
  const [sitemapModal, setSitemapModal] = useState({ isOpen: false });

  // Настройка сенсоров для Drag and Drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const decodeBase64 = (str) => {
    if (!str) return '';
    
    const isBase64 = /^[A-Za-z0-9+/]*={0,2}$/.test(str) && str.length % 4 === 0;
    
    if (isBase64) {
      try {
        const decoded = atob(str);
        try {
          return decodeURIComponent(escape(decoded));
        } catch {
          return decoded;
        }
      } catch {
        return str;
      }
    } else {
      return str;
    }
  };

  const parseKeywords = (keyData) => {
    if (!keyData) return [];
    
    try {
      const decoded = decodeBase64(keyData);
      const keywords = [];
      
      const matches = decoded.match(/"([^"]+)"/g);
      if (matches) {
        const extractedKeywords = matches.map(match => match.replace(/"/g, ''));
        keywords.push(...extractedKeywords.map(keyword => {
          try {
            return decodeURIComponent(escape(keyword));
          } catch {
            return keyword;
          }
        }));
      }
      
      if (keywords.length === 0) {
        const altMatches = decoded.match(/s:\d+:"([^"]+)"/g);
        if (altMatches) {
          const altKeywords = altMatches.map(match => {
            const content = match.match(/s:\d+:"([^"]+)"/);
            return content ? content[1] : '';
          }).filter(Boolean);
          
          keywords.push(...altKeywords.map(keyword => {
            try {
              return decodeURIComponent(escape(keyword));
            } catch {
              return keyword;
            }
          }));
        }
      }
      
      return keywords;
    } catch (error) {
      return [];
    }
  };

  const getInternalComment = (textTd) => {
    if (!textTd) {
      return '';
    }
    
    try {
      const decoded = atob(textTd);
      const textMatch = decoded.match(/text";a:\d+:{[^}]*}/);
      
      if (textMatch) {
        const textContent = textMatch[0];
        const itemMatches = textContent.match(/i:\d+;s:\d+:"([^"]+)"/g);
        
        if (itemMatches) {
          const comments = itemMatches.map(match => {
            const content = match.match(/i:\d+;s:\d+:"([^"]+)"/);
            if (content) {
              try {
                const decodedComment = decodeURIComponent(escape(content[1]));
                return decodedComment;
              } catch {
                return content[1];
              }
            }
            return '';
          }).filter(Boolean);
          
          return comments.join('\n');
        }
      }
      return '';
    } catch (error) {
      return '';
    }
  };

  useEffect(() => {
    if (page && isOpen) {
      const keywords = parseKeywords(page.key);
      
      setFormData({
        url: page.url || '',
        weight: page.weight || 0,
        text: page.text || getInternalComment(page.text_td) || '',
        keywords: keywords
      });
      setError(null);
    }
  }, [page, isOpen]);

  const encodeKeywordsToPHP = (keywords) => {
    if (!keywords || keywords.length === 0) return '';
    
    const encodedKeywords = keywords.map(keyword => {
      const encoded = keyword.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      return `s:${encoded.length}:"${encoded}"`;
    });
    
    return `a:${keywords.length}:{${encodedKeywords.map((kw, index) => `i:${index};${kw}`).join(';')}}`;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleKeywordChange = (index, value) => {
    const newKeywords = [...formData.keywords];
    newKeywords[index] = value;
    setFormData(prev => ({
      ...prev,
      keywords: newKeywords
    }));
  };

  const addKeyword = () => {
    setFormData(prev => ({
      ...prev,
      keywords: [...prev.keywords, '']
    }));
  };

  const removeKeyword = (index) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter((_, i) => i !== index)
    }));
  };

  // Функции для перемещения ключевых слов
  const moveKeywordUp = (index) => {
    if (index <= 0) return;
    
    const newKeywords = [...formData.keywords];
    const temp = newKeywords[index];
    newKeywords[index] = newKeywords[index - 1];
    newKeywords[index - 1] = temp;
    
    setFormData(prev => ({
      ...prev,
      keywords: newKeywords
    }));
  };

  const moveKeywordDown = (index) => {
    if (index >= formData.keywords.length - 1) return;
    
    const newKeywords = [...formData.keywords];
    const temp = newKeywords[index];
    newKeywords[index] = newKeywords[index + 1];
    newKeywords[index + 1] = temp;
    
    setFormData(prev => ({
      ...prev,
      keywords: newKeywords
    }));
  };

  const isFirstKeyword = (index) => {
    return index === 0;
  };

  const isLastKeyword = (index) => {
    return index === formData.keywords.length - 1;
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const requestData = {
        pages: [{
          id: page.id,
          url: formData.url,
          weight: parseInt(formData.weight),
          text: formData.text,
          key: formData.keywords.filter(kw => kw.trim())
        }]
      };

      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites/${siteId}/pages`, {
        method: 'PUT',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        onClose();
        
        // Обновляем данные на странице
        if (onPageUpdated) {
          onPageUpdated();
        }
      } else {
        setError(data.message || 'Ошибка сохранения');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError(error.message || 'Ошибка сети при сохранении');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    // Восстанавливаем данные из текущего состояния страницы
    const keywords = parseKeywords(page.key);
    setFormData({
      url: page.url || '',
      weight: page.weight || 0,
      text: page.text || getInternalComment(page.text_td) || '',
      keywords: keywords
    });
    onClose();
  };

  const handleUrlSelect = (url) => {
    setFormData(prev => ({
      ...prev,
      url: url
    }));
  };

  if (!isOpen || !page) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-auto max-h-[90vh] overflow-y-auto">
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-800">
                {currentLang === 'ru' ? 'Редактирование страницы' : 'Edit Page'}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-gray-600 p-1 transition-colors"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4 sm:space-y-5">
              {/* URL */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {currentLang === 'ru' ? 'URL:' : 'URL:'}
                  </label>
                  {site?.sitemaps && (
                    <button
                      type="button"
                      onClick={() => setSitemapModal({ isOpen: true })}
                      className="px-2.5 py-1.5 bg-blue-500 text-white text-xs rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-1"
                      title={currentLang === 'ru' ? 'Выбрать из sitemap' : 'Select from sitemap'}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span>{currentLang === 'ru' ? 'Sitemap' : 'Sitemap'}</span>
                    </button>
                  )}
                </div>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => handleInputChange('url', e.target.value)}
                  className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-colors"
                  placeholder={currentLang === 'ru' ? 'Введите URL' : 'Enter URL'}
                />
              </div>

              {/* Вес страницы */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLang === 'ru' ? 'Вес страницы:' : 'Page Weight:'}
                </label>
                <input
                  type="number"
                  value={formData.weight}
                  onChange={(e) => handleInputChange('weight', e.target.value)}
                  className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-colors"
                  min="0"
                  max="100"
                />
              </div>

              {/* Внутренний комментарий */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLang === 'ru' ? 'Внутренний комментарий:' : 'Internal Comment:'}
                </label>
                <textarea
                  value={formData.text}
                  onChange={(e) => handleInputChange('text', e.target.value)}
                  rows="3"
                  className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-colors resize-none"
                  placeholder={currentLang === 'ru' ? 'Введите комментарий к странице' : 'Enter page comment'}
                />
              </div>

              {/* Ключевые слова */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {currentLang === 'ru' ? 'Ключевые слова:' : 'Keywords:'}
                  </label>
                  <button
                    type="button"
                    onClick={addKeyword}
                    className="px-2.5 py-1.5 bg-green-500 text-white text-xs rounded-lg hover:bg-green-600 transition-colors"
                  >
                    {currentLang === 'ru' ? '+ Добавить' : '+ Add'}
                  </button>
                </div>

                {/* Подсказка для мобильных устройств */}
                {formData.keywords.length > 1 && (
                  <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg mb-3 sm:hidden">
                    <div className="flex items-center gap-2 text-xs text-blue-700">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{currentLang === 'ru' ? 'Используйте стрелки для перемещения' : 'Use arrows to move items'}</span>
                    </div>
                  </div>
                )}

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => {
                    const { active, over } = event;
                    
                    if (active && over && active.id !== over.id) {
                      const activeIndex = parseInt(active.id.split('-')[1]);
                      const overIndex = parseInt(over.id.split('-')[1]);
                      
                      if (!isNaN(activeIndex) && !isNaN(overIndex)) {
                        const newKeywords = arrayMove(formData.keywords, activeIndex, overIndex);
                        setFormData(prev => ({
                          ...prev,
                          keywords: newKeywords
                        }));
                      }
                    }
                  }}
                >
                  <SortableContext
                    items={formData.keywords.map((_, index) => `keyword-${index}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {formData.keywords.map((keyword, index) => (
                        <SortableKeywordItem
                          key={`keyword-${index}`}
                          keyword={keyword}
                          index={index}
                          onKeywordChange={handleKeywordChange}
                          onRemove={removeKeyword}
                          onMoveUp={moveKeywordUp}
                          onMoveDown={moveKeywordDown}
                          isFirst={isFirstKeyword(index)}
                          isLast={isLastKeyword(index)}
                          currentLang={currentLang}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            </div>

            {/* Кнопки действий */}
            <div className="flex justify-end space-x-2 sm:space-x-3 mt-5 sm:mt-6">
              <button
                onClick={handleCancel}
                className="px-3 py-2 sm:px-4 sm:py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                disabled={loading}
              >
                {currentLang === 'ru' ? 'Отмена' : 'Cancel'}
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-3 py-2 sm:px-4 sm:py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 text-sm"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-1.5 sm:space-x-2">
                    <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white"></div>
                    <span>{currentLang === 'ru' ? 'Сохранение...' : 'Saving...'}</span>
                  </div>
                ) : (
                  currentLang === 'ru' ? 'Сохранить' : 'Save'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Модалка выбора URL из sitemap */}
      <SitemapUrlModal
        site={site}
        isOpen={sitemapModal.isOpen}
        onClose={() => setSitemapModal({ isOpen: false })}
        onUrlSelect={handleUrlSelect}
      />
    </>
  );
};

export default EditPageModal; 